<template>
  <StepLayout
    class="mb-56"
    :slot-classes="{
      left: 'left-0 top-[78px] flex justify-center md:sticky',
      right: 'md:pt-48',
    }"
    stretch
    :tracking="trackingStepLayout"
    :without-left-template
  >
    <template #left>
      <Gallery
        :allow-media-viewer="false"
        :allow-partners-images="false"
        class="mb-16 hidden w-full md:block"
        :images
        :should-preload-first-img="false"
        :tracking-product-model="product.model"
        tracking-zone="pp_recap_carousel"
      />
    </template>

    <template #right>
      <ProductRecapCard
        :add-to-cart-label="i18n(translations.resultAddToCart)"
        :options="resolveRecapOptions(selectedOptions, upgradePickerFields)"
        :product
        :selected-color-code
        @add-to-cart="onAddToCart"
      >
        <template #price>
          <Price
            :mobile-plan
            :price
            :price-due-today="priceWithoutSubsidies"
            :product
            :selected-offer
            :with-assurance-fee="withQualityAssuranceFee"
          />
        </template>

        <template #addition>
          <MobilePlanAddition
            v-if="mobilePlan"
            class="mt-12"
            :plan="mobilePlan"
          />
        </template>
      </ProductRecapCard>

      <!-- Negative margin for the accordion item -->
      <div
        class="-ml-24 w-[calc(100%+48px)] md:ml-auto md:w-auto"
        :class="{ 'mb-16': showSellerRating }"
      >
        <AttractiveReturnsAccordion
          :tracking="{ zone: 'return_policy', name: '30_days_free_trial' }"
        />
      </div>

      <template v-if="!showSellerRating">
        <RevDivider class="mb-8" />

        <Seller :category="product.type" :selected-offer />
      </template>

      <ClientOnly>
        <p>
          <PaymentIcons :methods="[...paymentMethods, ...paymentMethods]" />
        </p>
      </ClientOnly>

      <!-- TODO: refactor to use the same component as the one at the top -->
      <div class="body-2-bold my-24 flex flex-col gap-8">
        <div v-for="item in product.reassuranceBlocks" :key="item.type">
          <ItemObsolete v-if="item.type !== 'active'" :item />
        </div>
        <DeliveryPromise
          :merchant="selectedOffer.merchant"
          :shipping="selectedOffer.shipping"
          tracking-context="recap"
        />
        <ItemCarrier
          :carrier="product.carrier"
          :product-brand="product.brand"
          :product-category="product.tracking.categoryId"
          :tracking="{
            zone: 'carrier_compatibility',
            name: 'carrier_info_recap',
          }"
        />
        <ItemReassurance
          :tracking="{ zone: 'return_info', name: '30_days_return_recap' }"
          :warranty-delay="selectedOffer.defaultWarrantyDelay"
        />
        <ItemSellerRating
          v-if="showSellerRating"
          :listing-id="selectedOffer.offerId"
          :merchant="selectedOffer.merchant"
          :tracking="{ zone: 'seller_rating', name: 'seller_rating_recap' }"
        />

        <ContainerVat class="mt-24" />
      </div>

      <slot name="details" />
    </template>
  </StepLayout>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Price as PriceType } from '@backmarket/http-api/src/api-models/Price'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import PaymentIcons from '@backmarket/nuxt-layer-payment/methods/PaymentIcons'
import { usePaymentMethodsWithFallback } from '@backmarket/nuxt-layer-payment/methods/composables/usePaymentMethodsWithFallback'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevDivider } from '@ds/components/Divider'

import { useAddToCart } from '~/scopes/product-core/components/containers/ContainerAddToCart/composables/useAddToCart/useAddToCart'
import ContainerVat from '~/scopes/product-core/components/containers/ContainerVat/ContainerVat.vue'
import AttractiveReturnsAccordion from '~/scopes/product-core/components/presentationals/AttractiveReturnsAccordion/AttractiveReturnsAccordion.vue'
import Gallery from '~/scopes/product-core/components/presentationals/Gallery/Gallery.vue'
import Price from '~/scopes/product-core/components/presentationals/Price/Price.vue'
import ProductRecapCard from '~/scopes/product-core/components/presentationals/ProductRecapCard/ProductRecapCard.vue'
import { resolveRecapOptions } from '~/scopes/product-core/components/presentationals/ProductRecapCard/utils/resolveRecapOptions'
import ItemCarrier from '~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemCarrier/ItemCarrier.vue'
import ItemObsolete from '~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemObsolete/ItemObsolete.vue'
import ItemReassurance from '~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemReassurance/ItemReassurance.vue'
import ItemSellerRating from '~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemSellerRating/ItemSellerRating.vue'
import Seller from '~/scopes/product-core/components/presentationals/Seller/Seller.vue'
import { useProductTracking } from '~/scopes/product-core/composables/useFetch/useFetchProductTracking/composables/useProductTracking/useProductTracking'
import { useQualityServiceFeeEnabled } from '~/scopes/product-core/composables/useQualityServiceFeeEnabled/useQualityServiceFeeEnabled'
import type { PartnerNames } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/types'
import DeliveryPromise from '~/scopes/shipping/components/DeliveryPromise/DeliveryPromise.vue'

import StepLayout from '../StepLayout/StepLayout.vue'

import translations from './Proposal.translations'
import MobilePlanAddition from './components/MobilePlanAddition/MobilePlanAddition.vue'

const props = withDefaults(
  defineProps<{
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    isFocused?: boolean
    product: GetProductResponse
    price: PriceType
    priceWithoutSubsidies?: PriceType | null
    mobilePlan?: MobilePlanOffer
    selectedOptions: Array<string>
    selectedColorCode?: string
    tracking: ReturnType<typeof useProductTracking>
    withoutLeftTemplate?: boolean
    upgradePickerFields?: NonNullable<
      GetPickersResponse['upgrade']
    >['pickerFields']
    telcoSelection?: {
      isPickerActive: boolean
      selectedPartnerName: PartnerNames | null
    }
  }>(),
  {
    isFocused: true,
    selectedColorCode: undefined,
    mobilePlan: undefined,
    priceWithoutSubsidies: undefined,
    withoutLeftTemplate: false,
    upgradePickerFields: undefined,
    telcoSelection: undefined,
  },
)

const experiments = useExperiments()
const { addToCart } = useAddToCart()

const i18n = useI18n()

const showSellerRating =
  experiments['experiment.sellerRatingPDP'] === 'withSellerRating'

const { isQualityServiceFeeEnabled: withQualityAssuranceFee } =
  useQualityServiceFeeEnabled()

const paymentMethods = usePaymentMethodsWithFallback(
  () => props.selectedOffer.paymentMethods,
)

const images = computed(() => {
  return props.product.images.slice(0, 3)
})

const trackingStepLayout = computed(() => {
  return {
    trackingId: 'recap',
    trackingModel: props.product.model,
    trackingCategory: props.product.tracking.categoryName,
  }
})

function onAddToCart() {
  addToCart({
    listingId: props.selectedOffer.offerLegacyId,
    listingPublicId: props.selectedOffer.offerId,
    tracking: {
      ...props.tracking.product.value,
      list: 'recap',
      campaign_id: props.selectedOffer.campaignId,
      population_id: props.selectedOffer.populationId,
    },
    partnerPromoCodes: props.product.includedServiceOffers.partnerPromoCodes,
    isTradeInEligible: props.product.isTradeInEligible,
    promoCodes: props.product.promoCodes,
    telcoSelection: props.telcoSelection,
  })
}
</script>
